import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
  useLayoutEffect,
} from 'react';
import { HiChevronDown, HiSearch } from 'react-icons/hi';
import apiService from '../services/api';
import { normalizeApiData } from '../utils/apiHelpers';
import './ClientSearchSelect.css';

const norm = (s) =>
  (s || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

function clientDisplayName(c) {
  return (c?.nome || c?.name || '').trim();
}

function parseCreatedClient(res) {
  if (!res) return null;
  if (res.id) {
    return {
      id: res.id,
      nome: res.nome || res.name || '',
    };
  }
  const inner = res.data;
  if (inner && typeof inner === 'object' && inner.id) {
    return {
      id: inner.id,
      nome: inner.nome || inner.name || '',
    };
  }
  const arr = normalizeApiData(res);
  if (Array.isArray(arr) && arr[0]?.id) {
    const c = arr[0];
    return { id: c.id, nome: c.nome || c.name || '' };
  }
  return null;
}

const ClientSearchSelect = ({
  value,
  onChange,
  clients = [],
  selectedNome = '',
  onRefreshClients,
  invalid = false,
  id = 'field-cliente_id',
  disabled = false,
  placeholder = 'Selecione ou busque um cliente',
  onNotify,
  /** 'id' (caixa/mala) ou 'nome' (fatura usa nome como valor do formulário) */
  valueMode = 'id',
  /** Texto para dicas ao criar cliente, ex.: "caixa/mala" ou "fatura" */
  linkEntityLabel = 'caixa/mala',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createTelefone, setCreateTelefone] = useState('');
  const [createDocumento, setCreateDocumento] = useState('');
  const [createEndereco, setCreateEndereco] = useState('');
  const [creating, setCreating] = useState(false);
  const [placement, setPlacement] = useState('bottom');
  const wrapRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const sortedClients = useMemo(() => {
    return [...clients].sort((a, b) =>
      clientDisplayName(a).localeCompare(clientDisplayName(b), 'pt-BR', {
        sensitivity: 'base',
      })
    );
  }, [clients]);

  const filtered = useMemo(() => {
    const q = norm(search);
    if (!q) return sortedClients;
    return sortedClients.filter((c) =>
      norm(clientDisplayName(c)).includes(q)
    );
  }, [sortedClients, search]);

  const selected = useMemo(() => {
    if (value === '' || value === null || value === undefined) return null;
    if (valueMode === 'id') {
      return clients.find((c) => String(c.id) === String(value)) || null;
    }
    const v = String(value).trim();
    return (
      clients.find((c) => clientDisplayName(c) === v) ||
      clients.find((c) => norm(clientDisplayName(c)) === norm(v)) ||
      null
    );
  }, [clients, value, valueMode]);

  const displayLabel = selected
    ? clientDisplayName(selected)
    : valueMode === 'nome' && String(value || '').trim()
      ? String(value).trim()
      : value && selectedNome
        ? selectedNome
        : '';

  // Enquanto o dropdown está aberto, mostrar no trigger o que está sendo digitado na busca
  // (senão parece que o "nome" sumiu — ficava só o placeholder ou o cliente anterior)
  const triggerDisplay =
    open && search !== '' ? search : displayLabel;

  const showCreate =
    open && search.trim().length > 0 && filtered.length === 0;

  const updatePlacement = useCallback(() => {
    if (!open || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const gap = 6;
    const maxH = Math.min(320, window.innerHeight * 0.55);
    const measured = dropdownRef.current?.offsetHeight ?? 0;
    const estH =
      measured > 0 ? Math.min(measured, maxH) : Math.min(260, maxH);
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    let next = 'bottom';
    if (spaceBelow >= estH) {
      next = 'bottom';
    } else if (spaceAbove >= estH) {
      next = 'top';
    } else if (spaceAbove > spaceBelow) {
      next = 'top';
    } else {
      next = 'bottom';
    }
    setPlacement(next);
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePlacement();
    const id = requestAnimationFrame(() => updatePlacement());
    return () => cancelAnimationFrame(id);
  }, [open, showCreate, filtered.length, search, clients.length, updatePlacement]);

  useEffect(() => {
    if (!open) return;
    const ro = new ResizeObserver(() => updatePlacement());
    if (dropdownRef.current) ro.observe(dropdownRef.current);
    window.addEventListener('resize', updatePlacement);
    window.addEventListener('scroll', updatePlacement, true);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updatePlacement);
      window.removeEventListener('scroll', updatePlacement, true);
    };
  }, [open, updatePlacement]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => searchInputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
        setCreateEmail('');
        setCreateTelefone('');
        setCreateDocumento('');
        setCreateEndereco('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setSearch('');
        setCreateEmail('');
        setCreateTelefone('');
        setCreateDocumento('');
        setCreateEndereco('');
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const selectClient = useCallback(
    (client) => {
      const payload = {
        cliente_id: String(client.id),
        cliente_nome: clientDisplayName(client),
      };
      if (Object.prototype.hasOwnProperty.call(client, 'email')) {
        payload.cliente_email = client.email || '';
      }
      if (Object.prototype.hasOwnProperty.call(client, 'telefone')) {
        payload.cliente_telefone = client.telefone || '';
      }
      if (Object.prototype.hasOwnProperty.call(client, 'documento')) {
        payload.cliente_documento = client.documento || '';
      }
      if (Object.prototype.hasOwnProperty.call(client, 'endereco')) {
        payload.cliente_endereco = client.endereco || '';
      }
      onChange(payload);
      setOpen(false);
      setSearch('');
      setCreateEmail('');
      setCreateTelefone('');
      setCreateDocumento('');
      setCreateEndereco('');
    },
    [onChange]
  );

  const handleCreateAndLink = async () => {
    const nome = search.trim();
    const email = createEmail.trim();
    if (!nome) {
      onNotify?.({ type: 'warning', message: 'Informe o nome na busca.' });
      return;
    }
    if (!email) {
      onNotify?.({ type: 'warning', message: 'E-mail é obrigatório para criar o cliente.' });
      return;
    }
    try {
      setCreating(true);
      const res = await apiService.createClient({
        nome,
        email,
        telefone: createTelefone.trim() || undefined,
        documento: createDocumento.trim() || undefined,
        endereco: createEndereco.trim() || undefined,
      });
      const created = parseCreatedClient(res);
      if (!created?.id) {
        throw new Error('Resposta inválida ao criar cliente');
      }
      await onRefreshClients?.();
      selectClient({
        id: created.id,
        nome: created.nome || nome,
        email: createEmail.trim(),
        telefone: createTelefone.trim() || '',
        documento: createDocumento.trim() || '',
        endereco: createEndereco.trim() || '',
      });
      onNotify?.({
        type: 'success',
        message: `Cliente criado e vinculado à ${linkEntityLabel}.`,
      });
    } catch (err) {
      onNotify?.({
        type: 'warning',
        message: err?.message || 'Erro ao criar cliente',
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="client-search-select" ref={wrapRef}>
      <button
        type="button"
        id={id}
        className={`client-search-select__trigger ${invalid ? 'field-invalid' : ''}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        <span
          className={`client-search-select__trigger-text ${!triggerDisplay ? 'client-search-select__trigger-text--placeholder' : ''}`}
        >
          {triggerDisplay || placeholder}
        </span>
        <HiChevronDown
          size={20}
          style={{
            flexShrink: 0,
            color: 'var(--text-secondary)',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s ease',
          }}
        />
      </button>

      {open && (
        <div
          ref={dropdownRef}
          className={`client-search-select__dropdown client-search-select__dropdown--${placement}`}
          role="listbox"
        >
          <div className="client-search-select__search-wrap">
            <HiSearch size={18} />
            <input
              ref={searchInputRef}
              type="text"
              className="client-search-select__search"
              placeholder="Buscar por nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
            />
          </div>

          <div className="client-search-select__list">
            {clients.length === 0 && !search.trim() && (
              <div className="client-search-select__empty">
                Nenhum cliente cadastrado. Digite o nome para buscar ou criar.
              </div>
            )}
            {filtered.map((c) => {
              const active = selected
                ? c.id === selected.id
                : valueMode === 'id' && String(c.id) === String(value);
              return (
                <button
                  key={c.id}
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={`client-search-select__option ${active ? 'client-search-select__option--active' : ''}`}
                  onClick={() => selectClient(c)}
                >
                  {clientDisplayName(c)}
                </button>
              );
            })}
            {filtered.length === 0 && search.trim().length > 0 && !showCreate && (
              <div className="client-search-select__empty">
                Nenhum cliente com esse nome.
              </div>
            )}
          </div>

          {showCreate && (
            <div className="client-search-select__create">
              <p className="client-search-select__create-title">
                Criar novo cliente
              </p>
              <p className="client-search-select__create-hint">
                Nome: <strong>{search.trim()}</strong> — preencha o e-mail para
                cadastrar e vincular a esta {linkEntityLabel}.
              </p>
              <div className="client-search-select__create-fields">
                <input
                  type="email"
                  placeholder="E-mail *"
                  value={createEmail}
                  onChange={(e) => setCreateEmail(e.target.value)}
                  autoComplete="email"
                />
                <input
                  type="text"
                  placeholder="Telefone (opcional)"
                  value={createTelefone}
                  onChange={(e) => setCreateTelefone(e.target.value)}
                  autoComplete="tel"
                />
                <input
                  type="text"
                  placeholder="Documento (opcional)"
                  value={createDocumento}
                  onChange={(e) => setCreateDocumento(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Endereço (opcional)"
                  value={createEndereco}
                  onChange={(e) => setCreateEndereco(e.target.value)}
                />
              </div>
              <div className="client-search-select__create-actions">
                <button
                  type="button"
                  className="client-search-select__btn-cancel"
                  onClick={() => {
                    setSearch('');
                    setCreateEmail('');
                    setCreateTelefone('');
                    setCreateDocumento('');
                    setCreateEndereco('');
                  }}
                >
                  Limpar busca
                </button>
                <button
                  type="button"
                  className="client-search-select__btn-create"
                  disabled={creating}
                  onClick={handleCreateAndLink}
                >
                  {creating ? 'Criando…' : 'Criar e vincular'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientSearchSelect;
